import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useMedplum } from '@medplum/react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  INACTIVITY_TIMEOUT,
  ABSOLUTE_SESSION_TIMEOUT,
  WARNING_DURATION,
  TOKEN_REFRESH_INTERVAL,
  TOKEN_GRACE_PERIOD,
  SESSION_START_KEY,
  LAST_ACTIVITY_KEY,
  SESSION_WARNING_KEY,
  CHECK_INTERVAL,
} from './constants'
import { sessionReducer, initialState } from './reducer'
import { clearRecentPatients } from '@/hooks/useRecentPatients'
import { clearSessionState } from '@/hooks/useSessionState'
import {
  clearBrokerSessionMarker,
  isBrokerSession,
  logoutBrokerSession,
  refreshBrokerSession,
  shouldRefreshBrokerSession,
} from '@/auth/brokerSession'

import { useActivityTracker } from './useActivityTracker'
import { SessionContext, SessionContextValue } from './context'

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const medplum = useMedplum()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [state, dispatch] = useReducer(sessionReducer, initialState)

  const stateRef = useRef(state)
  const isLoggingOutRef = useRef(false)
  const lastRefreshRef = useRef(0)

  stateRef.current = state

  const profile = medplum.getProfile()
  const isAuthenticated = !!profile

  // Broker-session reload restore (PD-1512): the refresh credential lives in an httpOnly
  // cookie, so nothing survives a reload in the Medplum client itself. When the marker
  // says the last login was a broker session, try one cookie refresh before rendering —
  // otherwise ProtectedRoute would bounce a still-valid session to /login.
  const [restoring, setRestoring] = useState(() => isBrokerSession())
  useEffect(() => {
    if (!restoring) return
    if (medplum.isAuthenticated()) {
      setRestoring(false)
      return
    }
    void refreshBrokerSession(medplum)
      .then((result) => {
        if (result === 'unauthorized') {
          clearBrokerSessionMarker()
        }
      })
      .catch(() => {})
      .finally(() => setRestoring(false))
    // mount-only: the restore races nothing — the timer effect below starts afterwards
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useActivityTracker(isAuthenticated, stateRef)

  const logout = useCallback(async () => {
    if (isLoggingOutRef.current) return
    isLoggingOutRef.current = true

    dispatch({ type: 'LOGGING_OUT' })

    const returnTo = window.location.pathname

    localStorage.removeItem(SESSION_START_KEY)
    localStorage.removeItem(LAST_ACTIVITY_KEY)
    localStorage.removeItem(SESSION_WARNING_KEY)
    clearRecentPatients()
    // Section memory is in-memory, so a logout without a page reload would
    // otherwise hand the next clinician on this tab the previous one's selected
    // patient, thread and conversation (PD-1206).
    clearSessionState()
    // The query cache outlives the session too. A read that never refetches on its own
    // (My Profile's header copy) would otherwise show the next member the previous
    // member's name and photo until they opened My Profile themselves.
    queryClient.clear()

    try {
      if (isBrokerSession()) {
        // Broker session (PD-1512): server-side revocation via the gateway; the Medplum
        // client holds only an access token, so clear() drops local state.
        await logoutBrokerSession()
        medplum.clear()
      } else {
        await medplum.signOut()
      }
    } catch {
      // proceed regardless
    }

    if (returnTo && returnTo !== '/login') {
      sessionStorage.setItem('returnTo', returnTo)
    }

    navigate('/login', { replace: true })
  }, [medplum, navigate, queryClient])

  const stayActive = useCallback(() => {
    const now = Date.now()
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
    localStorage.setItem(SESSION_WARNING_KEY, 'active')
    dispatch({ type: 'STAY_ACTIVE' })
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      Sentry.setUser(profile?.id ? { id: profile.id } : null)
      const now = Date.now()
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
      isLoggingOutRef.current = false

      const stored = localStorage.getItem(SESSION_START_KEY)
      if (!stored || now - Number(stored) >= ABSOLUTE_SESSION_TIMEOUT) {
        localStorage.setItem(SESSION_START_KEY, String(now))
      }

      const handleChange = () => {
        if (!medplum.isAuthenticated() && !isLoggingOutRef.current) {
          if (!window.location.pathname.match(/login|setpassword|resetpassword/i)) {
            logout()
          }
        }
      }

      medplum.addEventListener('change', handleChange)
      return () => {
        medplum.removeEventListener('change', handleChange)
      }
    } else {
      Sentry.setUser(null)
      const currentPath = window.location.pathname
      if (currentPath && !currentPath.match(/login|setpassword|resetpassword/i)) {
        sessionStorage.setItem('returnTo', currentPath)
      }
      dispatch({ type: 'RESET' })
      isLoggingOutRef.current = false
    }
  }, [isAuthenticated, medplum, logout])

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === SESSION_WARNING_KEY && e.newValue) {
        if (e.newValue === 'active') {
          dispatch({ type: 'STAY_ACTIVE' })
        } else {
          const [status, deadlineStr] = e.newValue.split(':')
          const deadline = Number(deadlineStr)
          if (status === 'inactivity-warning') {
            dispatch({ type: 'WARN_INACTIVITY', deadline })
          } else if (status === 'absolute-warning') {
            dispatch({ type: 'WARN_ABSOLUTE', deadline })
          }
        }
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  useEffect(() => {
    if (!isAuthenticated) return

    const interval = setInterval(() => {
      if (isLoggingOutRef.current) return

      const sessionStart = Number(localStorage.getItem(SESSION_START_KEY) || 0)
      if (!sessionStart) return

      const now = Date.now()

      if (now - lastRefreshRef.current >= TOKEN_REFRESH_INTERVAL) {
        lastRefreshRef.current = now
        if (isBrokerSession()) {
          // Broker session (PD-1512): refresh via the gateway. The session-kind marker —
          // not the LaunchDarkly flag — decides the path, so a flag flip never strands a
          // live session. 401 = server says the session is over (anchor expiry, idle,
          // revocation, reuse) → log out; transport failures just retry next tick.
          // Every broker call rotates the handle, so only call when due: token close to
          // expiry, or 30 min since the last refresh (keeps the 1h idle window fed).
          if (shouldRefreshBrokerSession(now)) {
            refreshBrokerSession(medplum)
              .then((result) => {
                if (result === 'unauthorized') {
                  logout()
                }
              })
              .catch(() => {})
          }
        } else {
          medplum.refreshIfExpired(TOKEN_GRACE_PERIOD).catch(() => {
            if (import.meta.env.DEV) {
              console.warn('[SessionProvider] Token refresh failed, will retry')
            }
          })
        }
      }

      const { status } = stateRef.current

      if (status === 'inactivity-warning' || status === 'absolute-warning') {
        const { deadline } = stateRef.current
        if (deadline && now >= deadline) {
          logout()
        }
        return
      }

      if (status !== 'active') return

      const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0)
      const inactiveTime = now - lastActivity
      const absoluteTime = now - sessionStart

      if (absoluteTime >= ABSOLUTE_SESSION_TIMEOUT) {
        logout()
        return
      }

      if (absoluteTime >= ABSOLUTE_SESSION_TIMEOUT - WARNING_DURATION) {
        const deadline = sessionStart + ABSOLUTE_SESSION_TIMEOUT
        dispatch({ type: 'WARN_ABSOLUTE', deadline })
        localStorage.setItem(SESSION_WARNING_KEY, `absolute-warning:${deadline}`)
        return
      }

      if (inactiveTime >= INACTIVITY_TIMEOUT) {
        logout()
        return
      }

      if (inactiveTime >= INACTIVITY_TIMEOUT - WARNING_DURATION) {
        const deadline = lastActivity + INACTIVITY_TIMEOUT
        dispatch({ type: 'WARN_INACTIVITY', deadline })
        localStorage.setItem(SESSION_WARNING_KEY, `inactivity-warning:${deadline}`)
      }
    }, CHECK_INTERVAL)

    return () => clearInterval(interval)
  }, [isAuthenticated, medplum, logout])

  const value = useMemo<SessionContextValue>(
    // The context contract is fire-and-forget (() => void); logout is async internally.
    () => ({
      status: state.status,
      deadline: state.deadline,
      logout: () => void logout(),
      stayActive,
    }),
    [state.status, state.deadline, logout, stayActive],
  )

  if (restoring) {
    return null
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}
